import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import ChipPopoverTrigger from "@/components/ChipPopoverTrigger";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { Popover, PopoverContent } from "@/components/ui/popover";

type ChipRangeValue = {
  lower: number | null;
  upper: number | null;
};

interface ChipRangeInputProps {
  label: React.ReactNode;
  min?: number;
  max?: number;
  onChange: (newFilter: ChipRangeValue) => unknown;
  value?: Array<number | null | undefined>;
  disabled?: boolean;
  startAddon?: React.ReactNode;
  defaultMin?: number;
  defaultMax?: number;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  /** When false, only the min field is shown (floor-only filters). Default true. */
  showMax?: boolean;
}

function parseCommitted(value?: Array<number | null | undefined>) {
  const lower =
    typeof value?.[0] === "number" && Number.isInteger(value[0])
      ? value[0]
      : undefined;
  const upper =
    typeof value?.[1] === "number" && Number.isInteger(value[1])
      ? value[1]
      : undefined;
  return { lower, upper };
}

function toDraft(n: number | undefined) {
  return n == null ? "" : n.toLocaleString("en-US");
}

function countDigits(value: string) {
  let count = 0;
  for (const char of value) {
    if (char >= "0" && char <= "9") count += 1;
  }
  return count;
}

function caretFromDigitCount(formatted: string, digitCount: number) {
  if (digitCount <= 0) return formatted.startsWith("-") ? 1 : 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] >= "0" && formatted[i] <= "9") {
      seen += 1;
      if (seen === digitCount) return i + 1;
    }
  }
  return formatted.length;
}

function sanitizeIntegerDraft(raw: string, allowNegative: boolean) {
  const source = raw.trim();
  const negative = allowNegative && source.startsWith("-");
  const withoutSign = negative ? source.slice(1) : source;
  const integerPart = withoutSign.split(".")[0];
  const digits = integerPart.replace(/\D/g, "");
  if (digits === "") return negative ? "-" : "";
  return `${negative ? "-" : ""}${digits}`;
}

function formatIntegerDraft(raw: string) {
  if (raw === "" || raw === "-") return raw;
  const n = Number.parseInt(raw, 10);
  return Number.isInteger(n) ? n.toLocaleString("en-US") : raw;
}

function parseIntegerDraft(raw: string) {
  const sanitized = sanitizeIntegerDraft(raw, true);
  if (sanitized === "" || sanitized === "-") return undefined;
  const n = Number.parseInt(sanitized, 10);
  return Number.isInteger(n) ? n : undefined;
}

function clampToBounds(n: number, min?: number, max?: number) {
  let next = n;
  if (min != null) next = Math.max(min, next);
  if (max != null) next = Math.min(max, next);
  return next;
}

function ChipRangeInput(props: ChipRangeInputProps) {
  const {
    label,
    min,
    max,
    onChange,
    value,
    disabled,
    startAddon,
    defaultMin,
    defaultMax,
    minPlaceholder,
    maxPlaceholder,
    showMax = true,
  } = props;
  const [open, setOpen] = useState(false);
  const committed = parseCommitted(value);
  const committedRef = useRef(committed);
  const [draftMin, setDraftMin] = useState(toDraft(committed.lower));
  const [draftMax, setDraftMax] = useState(toDraft(committed.upper));
  const draftMinRef = useRef(draftMin);
  const draftMaxRef = useRef(draftMax);
  const lastEditedRef = useRef<"min" | "max">("min");
  const maxInputRef = useRef<HTMLInputElement>(null);
  const pendingCaretRef = useRef<{ input: HTMLInputElement; pos: number } | null>(
    null
  );
  const allowNegative = min == null || min < 0;

  committedRef.current = committed;
  draftMinRef.current = draftMin;
  draftMaxRef.current = draftMax;

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current;
    if (!pending) return;
    pending.input.setSelectionRange(pending.pos, pending.pos);
    pendingCaretRef.current = null;
  });

  useEffect(() => {
    if (open) return;
    setDraftMin(toDraft(committed.lower));
    setDraftMax(toDraft(committed.upper));
  }, [committed.lower, committed.upper, open]);

  const readFormattedDraft = (input: HTMLInputElement) => {
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;
    const formatted = formatIntegerDraft(
      sanitizeIntegerDraft(raw, allowNegative)
    );
    pendingCaretRef.current = {
      input,
      pos: caretFromDigitCount(formatted, countDigits(raw.slice(0, caret))),
    };
    return formatted;
  };

  const boundsAreCompleteRange = min != null && max != null;
  const baselineLower = defaultMin ?? (boundsAreCompleteRange ? min : undefined);
  const baselineUpper = defaultMax ?? (boundsAreCompleteRange ? max : undefined);
  const hasCompleteBaseline = baselineLower != null && baselineUpper != null;
  const active = hasCompleteBaseline
    ? committed.lower != null &&
    committed.upper != null &&
    !(committed.lower === baselineLower && committed.upper === baselineUpper)
    : committed.lower != null || committed.upper != null;

  const parseRange = (
    minRaw: string,
    maxRaw: string,
    autoCorrectSide?: "min" | "max"
  ) => {
    let nextLower = parseIntegerDraft(minRaw);
    let nextUpper = parseIntegerDraft(maxRaw);

    if (nextLower != null) nextLower = clampToBounds(nextLower, min, max);
    if (nextUpper != null) nextUpper = clampToBounds(nextUpper, min, max);

    if (autoCorrectSide && nextLower != null && nextUpper != null) {
      if (autoCorrectSide === "max" && nextUpper < nextLower) {
        nextLower = nextUpper;
      }
      if (autoCorrectSide === "min" && nextLower > nextUpper) {
        nextUpper = nextLower;
      }
    }

    return { nextLower, nextUpper };
  };

  const emitRange = (
    nextLower: number | undefined,
    nextUpper: number | undefined,
    syncDrafts: boolean
  ) => {
    if (syncDrafts) {
      const minDraft = toDraft(nextLower);
      const maxDraft = toDraft(nextUpper);
      setDraftMin(minDraft);
      setDraftMax(maxDraft);
      draftMinRef.current = minDraft;
      draftMaxRef.current = maxDraft;
    }

    const current = committedRef.current;
    if (nextLower === current.lower && nextUpper === current.upper) return;

    onChange({
      lower: nextLower ?? null,
      upper: nextUpper ?? null,
    });
  };

  const applyLive = (minRaw: string, maxRaw: string) => {
    emitRange(parseIntegerDraft(minRaw), parseIntegerDraft(maxRaw), false);
  };

  const applyCommit = (
    side: "min" | "max",
    minRaw: string,
    maxRaw: string
  ) => {
    const { nextLower, nextUpper } = parseRange(minRaw, maxRaw, side);
    emitRange(nextLower, nextUpper, true);
  };

  const dismiss = () => {
    applyCommit(
      lastEditedRef.current,
      draftMinRef.current,
      draftMaxRef.current
    );
    setOpen(false);
  };

  const handleClear = () => {
    if (baselineLower != null && baselineUpper != null) {
      setDraftMin(toDraft(baselineLower));
      setDraftMax(toDraft(baselineUpper));
      onChange({ lower: baselineLower, upper: baselineUpper });
      return;
    }

    setDraftMin("");
    setDraftMax("");
    onChange({ lower: null, upper: null });
  };

  const renderAddon = () =>
    startAddon == null ? null : (
      <InputGroupAddon align="inline-start">
        {typeof startAddon === "string" ? (
          <InputGroupText>{startAddon}</InputGroupText>
        ) : (
          startAddon
        )}
      </InputGroupAddon>
    );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setDraftMin(toDraft(committed.lower));
          setDraftMax(toDraft(committed.upper));
          setOpen(true);
          return;
        }

        applyCommit(
          lastEditedRef.current,
          draftMinRef.current,
          draftMaxRef.current
        );
        setOpen(false);
      }}
    >
      <ChipPopoverTrigger
        active={active}
        clearable
        disabled={disabled}
        onClear={handleClear}
      >
        {label}
      </ChipPopoverTrigger>
      <PopoverContent
        align="center"
        side="bottom"
        className="w-auto p-4"
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
          event.preventDefault();
          dismiss();
        }}
      >
        {/* Row: min and max integer fields. Max is optional for floor-only chips. */}
        <div className="flex items-center gap-2">
          <InputGroup
            className="w-36"
            data-disabled={disabled ? "true" : undefined}
          >
            {renderAddon()}
            <InputGroupInput
              aria-label="Minimum"
              autoComplete="off"
              disabled={disabled}
              inputMode="numeric"
              placeholder={minPlaceholder}
              type="text"
              value={draftMin}
              onBlur={(event) =>
                applyCommit("min", event.currentTarget.value, draftMaxRef.current)
              }
              onChange={(event) => {
                const next = readFormattedDraft(event.currentTarget);
                lastEditedRef.current = "min";
                setDraftMin(next);
                draftMinRef.current = next;
                applyLive(next, draftMaxRef.current);
              }}
              onKeyDown={(event) => {
                if (showMax && event.key === "Tab" && !event.shiftKey) {
                  event.preventDefault();
                  maxInputRef.current?.focus();
                  return;
                }
                if (event.key === "Enter") {
                  event.preventDefault();
                  applyCommit("min", draftMinRef.current, draftMaxRef.current);
                  setOpen(false);
                }
              }}
            />
          </InputGroup>
          {showMax ? (
            <>
              <span className="text-muted-foreground">–</span>
              <InputGroup
                className="w-36"
                data-disabled={disabled ? "true" : undefined}
              >
                {renderAddon()}
                <InputGroupInput
                  ref={maxInputRef}
                  aria-label="Maximum"
                  autoComplete="off"
                  disabled={disabled}
                  inputMode="numeric"
                  placeholder={maxPlaceholder}
                  type="text"
                  value={draftMax}
                  onBlur={(event) =>
                    applyCommit("max", draftMinRef.current, event.currentTarget.value)
                  }
                  onChange={(event) => {
                    const next = readFormattedDraft(event.currentTarget);
                    lastEditedRef.current = "max";
                    setDraftMax(next);
                    draftMaxRef.current = next;
                    applyLive(draftMinRef.current, next);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      dismiss();
                    }
                  }}
                />
              </InputGroup>
            </>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ChipRangeInput;
